import React from 'react';
import { getStatusConfig, getStatusColor, getStatusText } from '../../utils/statusUtils';
import { Clock, AlertCircle, CheckCircle, XCircle } from 'lucide-react';

interface StatusBadgeProps {
  status: string;
  type: 'order' | 'priority' | 'user';
  persona?: 'customer' | 'business' | 'admin';
  size?: 'sm' | 'md' | 'lg';
  showIcon?: boolean;
  className?: string;
}

const StatusBadge: React.FC<StatusBadgeProps> = ({
  status = 'pending',
  type = 'order',
  persona = 'customer',
  size = 'md',
  showIcon = true,
  className = ''
}) => {
  try {
    console.log('StatusBadge received status:', status);
    
    // Check if this is a custom status (contains specific step names)
    const isCustomStatus = status.includes('Fabric') || status.includes('Cutting') || 
                          status.includes('Measurements') || status.includes('Stitching') || 
                          status.includes('Quality') || status.includes('Ready') || 
                          status.includes('Next:') || status.includes('Completed') ||
                          status.includes('Order Placed');
    
    console.log('StatusBadge isCustomStatus:', isCustomStatus);
    
    let config, colorClasses, text;
    
    if (isCustomStatus) {
      // For custom statuses, determine appropriate styling based on content
      let baseStatus = 'in_progress'; // default
      
      if (status.includes('Next:')) {
        baseStatus = 'pending'; // Use pending style for "Next:" steps
      } else if (status.includes('Completed') || status.includes('Ready')) {
        baseStatus = 'completed'; // Use completed style for finished steps
      } else if (status.includes('Fabric') || status.includes('Cutting') || 
                 status.includes('Stitching') || status.includes('Quality')) {
        baseStatus = 'in_progress'; // Use in_progress style for active steps
      }
      
      config = getStatusConfig(baseStatus, type);
      colorClasses = getStatusColor(baseStatus, type);
      text = status; // Use the custom text directly
    } else {
      // For standard statuses, use the normal lookup
      config = getStatusConfig(status, type);
      colorClasses = getStatusColor(status, type);
      text = getStatusText(status, type);
    }

  const sizeClasses = {
    sm: 'px-2 py-1 text-xs',
    md: 'px-2.5 py-0.5 text-xs',
    lg: 'px-3 py-1 text-sm'
  };

  const iconSize = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5'
  };

  const getIcon = () => {
    if (!showIcon) return null;
    
    const iconProps = { className: `${iconSize[size]} mr-1` };
    
    // For custom statuses, use more appropriate icons
    if (isCustomStatus) {
      if (status.includes('Order Placed')) {
        return <CheckCircle {...iconProps} />; // ✅ Order placed
      } else if (status.includes('Measurements')) {
        return <Clock {...iconProps} />; // 🕐 Measurements (time-based)
      } else if (status.includes('Fabric') || status.includes('Cutting')) {
        return <Clock {...iconProps} />; // 🕐 Fabric work (time-based)
      } else if (status.includes('Stitching')) {
        return <Clock {...iconProps} />; // 🕐 Stitching (time-based)
      } else if (status.includes('Quality')) {
        return <CheckCircle {...iconProps} />; // ✅ Quality check
      } else if (status.includes('Ready') || status.includes('Completed')) {
        return <CheckCircle {...iconProps} />; // ✅ Ready/Completed
      } else if (status.includes('Next:')) {
        return <Clock {...iconProps} />; // 🕐 Next step (pending)
      } else {
        return <Clock {...iconProps} />; // 🕐 Default for custom statuses
      }
    }
    
    // For standard statuses, use the normal icon logic
    switch (config.icon) {
      case 'clock':
        return <Clock {...iconProps} />;
      case 'alert-circle':
        return <AlertCircle {...iconProps} />;
      case 'check-circle':
        return <CheckCircle {...iconProps} />;
      case 'x-circle':
        return <XCircle {...iconProps} />;
      default:
        return <Clock {...iconProps} />;
    }
  };

    return (
      <span 
        className={`
          inline-flex items-center rounded-full font-medium border
          ${colorClasses}
          ${sizeClasses[size]}
          ${className}
        `}
      >
        {getIcon()}
        {text}
      </span>
    );
  } catch (_error) {
    console.error('StatusBadge error:', error);
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200">
        {status || 'Unknown'}
      </span>
    );
  }
};

export default StatusBadge;
